import { supabase } from '@/lib/supabase'
import type {
  FuelTransactionTableRow,
  FuelTransactionFormData,
  FuelTransactionFilters,
  FuelType,
  FuelPricingType,
  FuelTransactionSource,
  FuelTransactionStatus,
  AircraftFuelSummary,
  FuelCommitmentProgress,
} from '@/types/fuel-transactions'
import { calculateFuelTotal, getDaysRemainingInMonth } from '@/types/fuel-transactions'
import type { RecordStatusFilter } from '@/types/record-status'
import { applyRecordStatusFilter } from './helpers/apply-record-status-filter'
import { restoreRecord } from './helpers/restore-record'

/**
 * Fetch all fuel transactions with optional filtering
 * Includes joins to clients, aircraft, locations, lease_contracts
 */
export async function fetchFuelTransactions(
  organizationId: string,
  filters?: FuelTransactionFilters
): Promise<FuelTransactionTableRow[]> {
  const recordStatus: RecordStatusFilter = filters?.recordStatus ?? 'active'

  let query = supabase
    .from('fuel_transactions')
    .select(`
      id,
      organization_id,
      location_id,
      transaction_number,
      client_id,
      aircraft_id,
      lease_contract_id,
      transient_stay_id,
      fuel_type,
      quantity_gallons,
      price_per_gallon,
      total_amount,
      pricing_type,
      fueled_at,
      truck_id,
      fueler_name,
      meter_start,
      meter_end,
      source,
      external_reference,
      external_system,
      status,
      notes,
      created_at,
      updated_at,
      deleted_at,
      client:client_id (
        client_type,
        first_name,
        last_name,
        company_name
      ),
      aircraft:aircraft_id (
        tail_number,
        aircraft_types:aircraft_type_id (
          manufacturer,
          model
        )
      ),
      location:location_id (
        name
      ),
      lease_contract:lease_contract_id (
        contract_number
      ),
      transient_stay:transient_stay_id (
        booking_number
      )
    `)
    .eq('organization_id', organizationId)

  query = applyRecordStatusFilter(query, recordStatus)

  query = query.order('fueled_at', { ascending: false })

  // Apply filters
  if (filters?.fuelType && filters.fuelType.length > 0) {
    query = query.in('fuel_type', filters.fuelType)
  }

  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status)
  }

  if (filters?.source && filters.source.length > 0) {
    query = query.in('source', filters.source)
  }

  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId)
  }

  if (filters?.aircraftId) {
    query = query.eq('aircraft_id', filters.aircraftId)
  }

  if (filters?.clientId) {
    query = query.eq('client_id', filters.clientId)
  }

  if (filters?.contractId) {
    query = query.eq('lease_contract_id', filters.contractId)
  }

  if (filters?.dateFrom) {
    query = query.gte('fueled_at', filters.dateFrom)
  }

  if (filters?.dateTo) {
    query = query.lte('fueled_at', filters.dateTo)
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase()
    query = query.or(`transaction_number.ilike.%${search}%`)
  }

  const { data, error } = await query

  if (error) throw error

  // Transform joined data to flat structure
  return (data ?? []).map((row): FuelTransactionTableRow => {
    const client = row.client as unknown as {
      client_type: string
      first_name: string | null
      last_name: string | null
      company_name: string | null
    } | null

    const clientName = client?.client_type === 'individual'
      ? [client.first_name, client.last_name].filter(Boolean).join(' ') || null
      : client?.company_name ?? null

    const aircraft = row.aircraft as unknown as {
      tail_number: string
      aircraft_types: { manufacturer: string; model: string } | null
    } | null

    const location = row.location as unknown as { name: string } | null
    const leaseContract = row.lease_contract as unknown as { contract_number: string } | null
    const transientStay = (row as Record<string, unknown>).transient_stay as { booking_number: string } | null

    return {
      id: row.id,
      organization_id: row.organization_id,
      location_id: row.location_id,
      transaction_number: row.transaction_number,
      client_id: row.client_id,
      client_name: clientName,
      aircraft_id: row.aircraft_id,
      tail_number: aircraft?.tail_number ?? null,
      aircraft_type_name: aircraft?.aircraft_types
        ? `${aircraft.aircraft_types.manufacturer} ${aircraft.aircraft_types.model}`
        : null,
      lease_contract_id: row.lease_contract_id,
      contract_number: leaseContract?.contract_number ?? null,
      transient_stay_id: (row as Record<string, unknown>).transient_stay_id as string | null ?? null,
      booking_number: transientStay?.booking_number ?? null,
      location_name: location?.name ?? null,
      fuel_type: row.fuel_type as FuelType,
      quantity_gallons: row.quantity_gallons,
      price_per_gallon: row.price_per_gallon,
      total_amount: row.total_amount,
      pricing_type: row.pricing_type as FuelPricingType | null,
      fueled_at: row.fueled_at,
      truck_id: row.truck_id,
      fueler_name: row.fueler_name,
      meter_start: row.meter_start,
      meter_end: row.meter_end,
      source: row.source as FuelTransactionSource,
      external_reference: row.external_reference,
      external_system: row.external_system,
      status: row.status as FuelTransactionStatus,
      notes: row.notes,
      created_at: row.created_at,
      updated_at: row.updated_at,
      deleted_at: (row as Record<string, unknown>).deleted_at as string | null ?? null,
    }
  })
}

/**
 * Fetch a single fuel transaction by ID with full details
 */
export async function fetchFuelTransactionById(id: string): Promise<FuelTransactionTableRow> {
  const { data, error } = await supabase
    .from('fuel_transactions')
    .select(`
      id,
      organization_id,
      location_id,
      transaction_number,
      client_id,
      aircraft_id,
      lease_contract_id,
      transient_stay_id,
      fuel_type,
      quantity_gallons,
      price_per_gallon,
      total_amount,
      pricing_type,
      fueled_at,
      truck_id,
      fueler_name,
      meter_start,
      meter_end,
      source,
      external_reference,
      external_system,
      status,
      notes,
      created_at,
      updated_at,
      deleted_at,
      client:client_id (
        client_type,
        first_name,
        last_name,
        company_name
      ),
      aircraft:aircraft_id (
        tail_number,
        aircraft_types:aircraft_type_id (
          manufacturer,
          model
        )
      ),
      location:location_id (
        name
      ),
      lease_contract:lease_contract_id (
        contract_number
      ),
      transient_stay:transient_stay_id (
        booking_number
      )
    `)
    .eq('id', id)
    .single()

  if (error) throw error

  const client = data.client as unknown as {
    client_type: string
    first_name: string | null
    last_name: string | null
    company_name: string | null
  } | null

  const clientName = client?.client_type === 'individual'
    ? [client.first_name, client.last_name].filter(Boolean).join(' ') || null
    : client?.company_name ?? null

  const aircraft = data.aircraft as unknown as {
    tail_number: string
    aircraft_types: { manufacturer: string; model: string } | null
  } | null

  const location = data.location as unknown as { name: string } | null
  const leaseContract = data.lease_contract as unknown as { contract_number: string } | null
  const transientStay = (data as Record<string, unknown>).transient_stay as { booking_number: string } | null

  return {
    id: data.id,
    organization_id: data.organization_id,
    location_id: data.location_id,
    transaction_number: data.transaction_number,
    client_id: data.client_id,
    client_name: clientName,
    aircraft_id: data.aircraft_id,
    tail_number: aircraft?.tail_number ?? null,
    aircraft_type_name: aircraft?.aircraft_types
      ? `${aircraft.aircraft_types.manufacturer} ${aircraft.aircraft_types.model}`
      : null,
    lease_contract_id: data.lease_contract_id,
    contract_number: leaseContract?.contract_number ?? null,
    transient_stay_id: (data as Record<string, unknown>).transient_stay_id as string | null ?? null,
    booking_number: transientStay?.booking_number ?? null,
    location_name: location?.name ?? null,
    fuel_type: data.fuel_type as FuelType,
    quantity_gallons: data.quantity_gallons,
    price_per_gallon: data.price_per_gallon,
    total_amount: data.total_amount,
    pricing_type: data.pricing_type as FuelPricingType | null,
    fueled_at: data.fueled_at,
    truck_id: data.truck_id,
    fueler_name: data.fueler_name,
    meter_start: data.meter_start,
    meter_end: data.meter_end,
    source: data.source as FuelTransactionSource,
    external_reference: data.external_reference,
    external_system: data.external_system,
    status: data.status as FuelTransactionStatus,
    notes: data.notes,
    created_at: data.created_at,
    updated_at: data.updated_at,
    deleted_at: (data as Record<string, unknown>).deleted_at as string | null ?? null,
  }
}

/**
 * Create a new fuel transaction
 * transaction_number is auto-generated by database trigger
 * total_amount is calculated from quantity and price
 */
export async function createFuelTransaction(
  organizationId: string,
  data: FuelTransactionFormData
): Promise<FuelTransactionTableRow> {
  const totalAmount = calculateFuelTotal(data.quantity_gallons, data.price_per_gallon)

  const { data: created, error } = await supabase
    .from('fuel_transactions')
    .insert({
      organization_id: organizationId,
      location_id: data.location_id,
      client_id: data.client_id,
      aircraft_id: data.aircraft_id,
      lease_contract_id: data.lease_contract_id ?? null,
      transient_stay_id: data.transient_stay_id ?? null,
      fuel_type: data.fuel_type,
      quantity_gallons: data.quantity_gallons,
      price_per_gallon: data.price_per_gallon,
      total_amount: totalAmount,
      pricing_type: data.pricing_type ?? 'contract',
      fueled_at: data.fueled_at,
      truck_id: data.truck_id ?? null,
      fueler_name: data.fueler_name ?? null,
      meter_start: data.meter_start ?? null,
      meter_end: data.meter_end ?? null,
      source: data.source ?? 'manual',
      external_reference: data.external_reference ?? null,
      external_system: data.external_system ?? null,
      status: data.status ?? 'completed',
      notes: data.notes ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
    .select()
    .single()

  if (error) throw error

  // Fetch with full joins for return
  return fetchFuelTransactionById(created.id)
}

/**
 * Update an existing fuel transaction
 */
export async function updateFuelTransaction(
  id: string,
  data: Partial<FuelTransactionFormData>
): Promise<FuelTransactionTableRow> {
  const updateData: Record<string, unknown> = {}

  // Only include fields that are explicitly provided
  if (data.location_id !== undefined) updateData.location_id = data.location_id
  if (data.client_id !== undefined) updateData.client_id = data.client_id
  if (data.aircraft_id !== undefined) updateData.aircraft_id = data.aircraft_id
  if (data.lease_contract_id !== undefined) updateData.lease_contract_id = data.lease_contract_id
  if (data.transient_stay_id !== undefined) updateData.transient_stay_id = data.transient_stay_id
  if (data.fuel_type !== undefined) updateData.fuel_type = data.fuel_type
  if (data.quantity_gallons !== undefined) updateData.quantity_gallons = data.quantity_gallons
  if (data.price_per_gallon !== undefined) updateData.price_per_gallon = data.price_per_gallon
  if (data.pricing_type !== undefined) updateData.pricing_type = data.pricing_type
  if (data.fueled_at !== undefined) updateData.fueled_at = data.fueled_at
  if (data.truck_id !== undefined) updateData.truck_id = data.truck_id
  if (data.fueler_name !== undefined) updateData.fueler_name = data.fueler_name
  if (data.meter_start !== undefined) updateData.meter_start = data.meter_start
  if (data.meter_end !== undefined) updateData.meter_end = data.meter_end
  if (data.source !== undefined) updateData.source = data.source
  if (data.external_reference !== undefined) updateData.external_reference = data.external_reference
  if (data.external_system !== undefined) updateData.external_system = data.external_system
  if (data.status !== undefined) updateData.status = data.status
  if (data.notes !== undefined) updateData.notes = data.notes

  // Recalculate total if quantity or price changed
  if (data.quantity_gallons !== undefined && data.price_per_gallon !== undefined) {
    updateData.total_amount = calculateFuelTotal(data.quantity_gallons, data.price_per_gallon)
  }

  const { error } = await supabase
    .from('fuel_transactions')
    .update(updateData)
    .eq('id', id)

  if (error) throw error

  // Fetch with full joins for return
  return fetchFuelTransactionById(id)
}

/**
 * Soft delete a fuel transaction
 */
export async function deleteFuelTransaction(id: string): Promise<void> {
  const { error } = await supabase
    .from('fuel_transactions')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Restore a soft-deleted fuel transaction
 */
export async function restoreFuelTransaction(id: string): Promise<void> {
  return restoreRecord('fuel_transactions', id)
}

/**
 * Fetch fuel summary for a specific aircraft (for FuelTab in ExpandingCard)
 * Includes monthly totals, YTD totals, commitment info, and recent transactions
 */
export async function fetchAircraftFuelSummary(
  aircraftId: string,
  contractId?: string
): Promise<AircraftFuelSummary> {
  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
  const yearStart = new Date(now.getFullYear(), 0, 1).toISOString()

  // Fetch aircraft info
  const { data: aircraft } = await supabase
    .from('aircraft')
    .select('tail_number')
    .eq('id', aircraftId)
    .single()

  // Fetch commitment info from lease contract if provided
  let commitmentInfo: {
    hasCommitment: boolean
    commitmentGallonsMonthly: number | null
    commitmentPricePerGallon: number | null
  } = {
    hasCommitment: false,
    commitmentGallonsMonthly: null,
    commitmentPricePerGallon: null,
  }

  if (contractId) {
    const { data: contract } = await supabase
      .from('lease_contracts')
      .select('fuel_commitment_gallons_monthly, fuel_price_per_gallon')
      .eq('id', contractId)
      .single()

    if (contract?.fuel_commitment_gallons_monthly) {
      commitmentInfo = {
        hasCommitment: true,
        commitmentGallonsMonthly: contract.fuel_commitment_gallons_monthly,
        commitmentPricePerGallon: contract.fuel_price_per_gallon,
      }
    }
  }

  // Fetch monthly totals
  const { data: monthlyData } = await supabase
    .from('fuel_transactions')
    .select('quantity_gallons, total_amount')
    .eq('aircraft_id', aircraftId)
    .eq('status', 'completed')
    .is('deleted_at', null)
    .gte('fueled_at', monthStart)

  const monthlyGallons = monthlyData?.reduce((sum, t) => sum + (t.quantity_gallons || 0), 0) ?? 0
  const monthlyRevenue = monthlyData?.reduce((sum, t) => sum + (t.total_amount || 0), 0) ?? 0
  const monthlyTransactionCount = monthlyData?.length ?? 0

  // Fetch YTD totals
  const { data: ytdData } = await supabase
    .from('fuel_transactions')
    .select('quantity_gallons, total_amount')
    .eq('aircraft_id', aircraftId)
    .eq('status', 'completed')
    .is('deleted_at', null)
    .gte('fueled_at', yearStart)

  const ytdGallons = ytdData?.reduce((sum, t) => sum + (t.quantity_gallons || 0), 0) ?? 0
  const ytdRevenue = ytdData?.reduce((sum, t) => sum + (t.total_amount || 0), 0) ?? 0
  const ytdTransactionCount = ytdData?.length ?? 0

  // Fetch recent transactions (last 5)
  const { data: recentData } = await supabase
    .from('fuel_transactions')
    .select(`
      id,
      organization_id,
      location_id,
      transaction_number,
      client_id,
      aircraft_id,
      lease_contract_id,
      fuel_type,
      quantity_gallons,
      price_per_gallon,
      total_amount,
      pricing_type,
      fueled_at,
      truck_id,
      fueler_name,
      meter_start,
      meter_end,
      source,
      external_reference,
      external_system,
      status,
      notes,
      created_at,
      updated_at
    `)
    .eq('aircraft_id', aircraftId)
    .eq('status', 'completed')
    .is('deleted_at', null)
    .order('fueled_at', { ascending: false })
    .limit(5)

  const recentTransactions: FuelTransactionTableRow[] = (recentData ?? []).map((row) => ({
    id: row.id,
    organization_id: row.organization_id,
    location_id: row.location_id,
    transaction_number: row.transaction_number,
    client_id: row.client_id,
    client_name: null,
    aircraft_id: row.aircraft_id,
    tail_number: aircraft?.tail_number ?? null,
    aircraft_type_name: null,
    lease_contract_id: row.lease_contract_id,
    contract_number: null,
    transient_stay_id: (row as Record<string, unknown>).transient_stay_id as string | null ?? null,
    booking_number: null,
    location_name: null,
    fuel_type: row.fuel_type as FuelType,
    quantity_gallons: row.quantity_gallons,
    price_per_gallon: row.price_per_gallon,
    total_amount: row.total_amount,
    pricing_type: row.pricing_type as FuelPricingType | null,
    fueled_at: row.fueled_at,
    truck_id: row.truck_id,
    fueler_name: row.fueler_name,
    meter_start: row.meter_start,
    meter_end: row.meter_end,
    source: row.source as FuelTransactionSource,
    external_reference: row.external_reference,
    external_system: row.external_system,
    status: row.status as FuelTransactionStatus,
    notes: row.notes,
    created_at: row.created_at,
    updated_at: row.updated_at,
    deleted_at: null,
  }))

  return {
    aircraftId,
    tailNumber: aircraft?.tail_number ?? null,
    ...commitmentInfo,
    monthlyGallons,
    monthlyRevenue,
    monthlyTransactionCount,
    ytdGallons,
    ytdRevenue,
    ytdTransactionCount,
    recentTransactions,
  }
}

/**
 * Fetch fuel commitment progress for all contracts with fuel commitments
 * Used for commitment status dashboards and alerts
 */
export async function fetchFuelCommitmentProgress(
  organizationId: string,
  locationId?: string
): Promise<FuelCommitmentProgress[]> {
  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const daysRemaining = getDaysRemainingInMonth()

  // Fetch active contracts with fuel commitments
  let query = supabase
    .from('lease_contracts')
    .select(`
      id,
      contract_number,
      client_id,
      fuel_commitment_gallons_monthly,
      client:client_id (
        client_type,
        first_name,
        last_name,
        company_name
      ),
      contract_aircraft:contract_aircraft (
        aircraft:aircraft_id (
          tail_number
        )
      )
    `)
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .is('deleted_at', null)
    .not('fuel_commitment_gallons_monthly', 'is', null)

  if (locationId) {
    query = query.eq('location_id', locationId)
  }

  const { data: contracts, error } = await query

  if (error) throw error

  // For each contract, calculate delivered gallons this month
  const results: FuelCommitmentProgress[] = []

  for (const contract of contracts ?? []) {
    const committedGallons = contract.fuel_commitment_gallons_monthly || 0

    // Get primary aircraft tail number
    const contractAircraft = contract.contract_aircraft as unknown as Array<{
      aircraft: { tail_number: string } | null
    }> | null
    const tailNumber = contractAircraft?.[0]?.aircraft?.tail_number ?? null

    // Get client name
    const client = contract.client as unknown as {
      client_type: string
      first_name: string | null
      last_name: string | null
      company_name: string | null
    } | null

    const clientName = client?.client_type === 'individual'
      ? [client.first_name, client.last_name].filter(Boolean).join(' ') || null
      : client?.company_name ?? null

    // Fetch delivered gallons for this contract this month
    const { data: fuelData } = await supabase
      .from('fuel_transactions')
      .select('quantity_gallons')
      .eq('lease_contract_id', contract.id)
      .eq('status', 'completed')
      .is('deleted_at', null)
      .gte('fueled_at', monthStart.toISOString())
      .lte('fueled_at', monthEnd.toISOString())

    const deliveredGallons = fuelData?.reduce((sum, t) => sum + (t.quantity_gallons || 0), 0) ?? 0
    const remainingGallons = Math.max(0, committedGallons - deliveredGallons)
    const percentComplete = committedGallons > 0
      ? Math.min(100, Math.round((deliveredGallons / committedGallons) * 100))
      : 0
    const isAtRisk = percentComplete < 70 && daysRemaining <= 10

    results.push({
      contractId: contract.id,
      contractNumber: contract.contract_number,
      clientId: contract.client_id,
      clientName,
      tailNumber,
      committedGallons,
      deliveredGallons,
      remainingGallons,
      percentComplete,
      daysRemaining,
      periodStart: monthStart.toISOString(),
      periodEnd: monthEnd.toISOString(),
      isAtRisk,
    })
  }

  // Sort by percent complete (lowest first - at risk contracts on top)
  return results.sort((a, b) => a.percentComplete - b.percentComplete)
}

// ============================================
// CSV Import API Functions
// ============================================

/**
 * Bulk create fuel transactions in batches
 * Used by CSV import flow
 */
export async function bulkCreateFuelTransactions(
  organizationId: string,
  rows: FuelTransactionFormData[]
): Promise<{ inserted: number; errors: { index: number; message: string }[] }> {
  const BATCH_SIZE = 50
  let inserted = 0
  const errors: { index: number; message: string }[] = []

  for (let offset = 0; offset < rows.length; offset += BATCH_SIZE) {
    const batch = rows.slice(offset, offset + BATCH_SIZE)

    const insertData = batch.map((row) => ({
      organization_id: organizationId,
      location_id: row.location_id,
      client_id: row.client_id,
      aircraft_id: row.aircraft_id,
      lease_contract_id: row.lease_contract_id ?? null,
      transient_stay_id: row.transient_stay_id ?? null,
      fuel_type: row.fuel_type,
      quantity_gallons: row.quantity_gallons,
      price_per_gallon: row.price_per_gallon,
      total_amount: calculateFuelTotal(row.quantity_gallons, row.price_per_gallon),
      pricing_type: row.pricing_type ?? 'contract',
      fueled_at: row.fueled_at,
      source: 'csv_import' as const,
      external_reference: row.external_reference ?? null,
      external_system: row.external_system ?? null,
      status: row.status ?? 'completed',
      notes: row.notes ?? null,
    }))

     
    const { data, error } = await supabase
      .from('fuel_transactions')
      .insert(insertData as any)
      .select('id')

    if (error) {
      // Record error for each row in this batch
      for (let j = 0; j < batch.length; j++) {
        errors.push({ index: offset + j, message: error.message })
      }
    } else {
      inserted += data?.length ?? 0
    }
  }

  return { inserted, errors }
}

/**
 * Fetch existing external references for dedup checking
 * Returns a Set of external_reference values that already exist
 */
export async function fetchExistingExternalRefs(
  organizationId: string,
  externalSystem: string
): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('fuel_transactions')
    .select('external_reference')
    .eq('organization_id', organizationId)
    .eq('external_system', externalSystem)
    .not('external_reference', 'is', null)
    .is('deleted_at', null)

  if (error) throw error

  const refs = new Set<string>()
  for (const row of data ?? []) {
    if (row.external_reference) {
      refs.add(row.external_reference)
    }
  }
  return refs
}

/**
 * Fetch active lease contracts with their linked aircraft IDs
 * Used to auto-link fuel transactions to contracts during CSV import
 */
export async function fetchActiveContractsWithAircraft(
  organizationId: string
): Promise<{
  id: string
  contract_number: string
  client_id: string
  start_date: string | null
  end_date: string | null
  aircraft_ids: string[]
}[]> {
  const { data, error } = await supabase
    .from('lease_contracts')
    .select(`
      id,
      contract_number,
      client_id,
      start_date,
      end_date,
      contract_aircraft (
        aircraft_id
      )
    `)
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .is('deleted_at', null)

  if (error) throw error

  return (data ?? []).map((contract) => {
    const contractAircraft = contract.contract_aircraft as unknown as { aircraft_id: string }[] | null
    return {
      id: contract.id,
      contract_number: contract.contract_number,
      client_id: contract.client_id,
      start_date: contract.start_date,
      end_date: contract.end_date,
      aircraft_ids: contractAircraft?.map((ca) => ca.aircraft_id) ?? [],
    }
  })
}
